import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown, Check } from "lucide-react";

const LANGUAGES = [
    { code: "it", label: "Italiano", flag: "🇮🇹" },
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "de", label: "Deutsch", flag: "🇩🇪" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "es", label: "Español", flag: "🇪🇸" },
];

export default function LanguageSelector({ compact = false }) {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const current = LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) || LANGUAGES[0];

    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const change = (code) => {
        i18n.changeLanguage(code);
        setOpen(false);
    };

    return (
        <div ref={ref} className="relative" data-testid="language-selector">
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="language-selector-btn"
                className={`inline-flex items-center gap-1.5 border border-white/15 hover:border-gold/50 rounded-sm transition-colors text-white ${
                    compact ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-xs"
                }`}
                aria-label="Language"
            >
                <Globe size={14} className="text-gold/80" strokeWidth={1.6} />
                <span className="tracking-widest uppercase">{current.code}</span>
                <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-1.5 w-44 bg-ink-surface border border-white/15 rounded-sm shadow-xl py-1 z-50"
                    data-testid="language-dropdown"
                >
                    {LANGUAGES.map((l) => {
                        const active = l.code === i18n.resolvedLanguage;
                        return (
                            <button
                                key={l.code}
                                onClick={() => change(l.code)}
                                data-testid={`language-option-${l.code}`}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-white/5 transition-colors ${
                                    active ? "text-gold" : "text-gray-200"
                                }`}
                            >
                                <span className="text-base leading-none">{l.flag}</span>
                                <span className="flex-1">{l.label}</span>
                                {active && <Check size={13} className="text-gold" strokeWidth={2} />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
